import React, { useRef } from 'react';
import { useFormikContext } from 'formik';
import { Label } from 'components/atoms';
import { PiX } from 'react-icons/pi';
import './QueryFilterLabels.scss';

type LabelRenderResult = React.ReactNode | null | undefined | false;

type ClearStrategy = 'auto' | 'keep-type' | 'ui-select';

const emptyValueFor = (current: unknown, strategy: ClearStrategy = 'auto') => {
  if (current === undefined || current === null) return '';

  if (strategy === 'ui-select') return '';

  if (typeof current === 'boolean') return undefined;

  if (Array.isArray(current)) return [];

  if (current instanceof Date) return undefined;

  return '';
};

export type QueryFilterLabelFormatter<TValues extends Record<string, any>> = (args: {
  key: keyof TValues;
  value: TValues[keyof TValues];
  values: TValues;
}) => LabelRenderResult;

export type QueryFilterRemoveHandler<TValues extends Record<string, any>> = (args: {
  key: keyof TValues;
  value: TValues[keyof TValues];
  values: TValues;
  initialValues: TValues;
  setValues: (updater: (prev: TValues) => TValues) => void;
}) => void;

export type QueryFilterLabelsRegistry<TValues extends Record<string, any>> = Partial<
  Record<keyof TValues, QueryFilterLabelFormatter<TValues>>
>;

export type QueryFilterRemoveRegistry<TValues extends Record<string, any>> = Partial<
  Record<keyof TValues, QueryFilterRemoveHandler<TValues>>
>;

const defaultIsActiveValue = (value: unknown) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return true;
};

const defaultToLabelText = (value: unknown) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
};

type StoredConfig = {
  formatters: QueryFilterLabelsRegistry<any>;
  removers: QueryFilterRemoveRegistry<any>;
};

const REGISTRY = new Map<string, StoredConfig>();

export const registerQueryFilterLabels = <TValues extends Record<string, any>>(
  scope: string,
  formatters: QueryFilterLabelsRegistry<TValues>,
  removers?: QueryFilterRemoveRegistry<TValues>,
) => {
  const next: StoredConfig = {
    formatters: formatters as QueryFilterLabelsRegistry<any>,
    removers: (removers ?? {}) as QueryFilterRemoveRegistry<any>,
  };

  const existing = REGISTRY.get(scope);

  REGISTRY.set(scope, {
    formatters: { ...(existing?.formatters ?? {}), ...next.formatters },
    removers: { ...(existing?.removers ?? {}), ...next.removers },
  });
};

export const getQueryFilterLabelsConfig = <TValues extends Record<string, any>>(scope: string) => {
  const cfg = REGISTRY.get(scope);

  return {
    formatters: (cfg?.formatters ?? {}) as QueryFilterLabelsRegistry<TValues>,
    removers: (cfg?.removers ?? {}) as QueryFilterRemoveRegistry<TValues>,
  };
};

const typedObjectToArray = <T extends Record<string, any>>(obj: T) =>
  (Object.keys(obj) as Array<keyof T>).map((key) => ({ key, value: obj[key] }));

interface QueryFilterLabelsProps<TValues extends Record<string, any>> {
  scope: string;
  clearStrategyByKey?: Partial<Record<keyof TValues, ClearStrategy>>;
  isFormOpen?: boolean;
  hideWhenFormOpen?: boolean;
  onFiltersChange?: (values: TValues) => void;
  debounceMs?: number;
  isActiveValue?: (value: unknown, key: keyof TValues, values: TValues) => boolean;
  fallbackLabel?: (value: unknown, key: keyof TValues, values: TValues) => React.ReactNode;
}

const QueryFilterLabels = <TValues extends Record<string, any>>({
  scope,
  isFormOpen = false,
  hideWhenFormOpen = true,
  isActiveValue,
  fallbackLabel,
  debounceMs,
  onFiltersChange,
  clearStrategyByKey,
}: QueryFilterLabelsProps<TValues>) => {
  const { values, setValues, initialValues } = useFormikContext<TValues>();
  const { formatters, removers } = getQueryFilterLabelsConfig<TValues>(scope);

  const entries = typedObjectToArray(values);

  const visibleLabels = entries
    .map(({ key, value }) => {
      const fn = isActiveValue ?? ((v) => defaultIsActiveValue(v));
      const isActive = fn(value, key, values);
      if (!isActive) return null;

      const formatter = formatters[key];
      const rendered = formatter
        ? formatter({ key, value, values })
        : fallbackLabel
          ? fallbackLabel(value, key, values)
          : defaultToLabelText(value);

      if (rendered === null || rendered === undefined || rendered === false || rendered === '') {
        return null;
      }

      return { key, value, rendered };
    })
    .filter(Boolean) as Array<{
    key: keyof TValues;
    value: TValues[keyof TValues];
    rendered: React.ReactNode;
  }>;
  const timer = useRef<number | null>(null);

  const emitChange = (nextValues: TValues) => {
    if (!onFiltersChange) return;

    const ms = debounceMs ?? 0;

    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onFiltersChange(nextValues), ms);
  };

  const removeFilter = (key: keyof TValues, value: TValues[keyof TValues]) => {
    const customRemove = removers[key];
    if (customRemove) {
      customRemove({ key, value, values, initialValues, setValues });
      return;
    }

    const strategy = clearStrategyByKey?.[key] ?? 'auto';
    const cleared = emptyValueFor(value, strategy);

    setValues((prev) => ({ ...prev, [key]: cleared as any }));
  };

  if (!visibleLabels.length) return null;
  if (hideWhenFormOpen && isFormOpen) return null;

  return (
    <div className="query-filter-labels-wrapper">
      <div className="query-filter-labels">
        {visibleLabels.map(({ key, value }) => {
          const formatter = formatters[key];

          const rendered = formatter
            ? formatter({ key, value, values })
            : fallbackLabel
              ? fallbackLabel(value, key, values)
              : defaultToLabelText(value);

          if (rendered === null || rendered === undefined || rendered === false) return null;

          return (
            <Label
              key={String(key)}
              size="compact"
              className="query-filter-label"
              onClick={() => removeFilter(key, value)}
            >
              <PiX />
              <div className="label-text">{rendered}</div>
            </Label>
          );
        })}
      </div>
    </div>
  );
};

export default QueryFilterLabels;
